import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Phone, CalendarDays, FileText, Sparkles, Copy, ExternalLink, Lock, ShieldCheck, Unlock,
} from "lucide-react";
import { HospitalHeader, BrandedShell } from "@/components/BrandedLayout";
import { RecoveryCalendar } from "@/components/RecoveryCalendar";
import { VoiceCaptureButton } from "@/components/VoiceCaptureButton";
import { FooterDisclaimer } from "@/components/FooterDisclaimer";
import { NurseCallScheduler } from "@/components/NurseCallScheduler";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { riskLevel, RISK_STYLES, formatDate, timeAgo } from "@/lib/appUtils";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function PatientDetailForDoctor() {
  const { hospCode } = useBranding();
  const { patientId } = useParams();
  const [patient, setPatient] = useState(null);
  const [duration, setDuration] = useState(20);
  const [showFinalize, setShowFinalize] = useState(false);
  const [confirmedReview, setConfirmedReview] = useState(false);

  const load = async () => {
    const p = await api.getPatient(patientId);
    setPatient(p);
    setDuration(p.care_plan_duration || 20);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId]);

  if (!patient) return <BrandedShell><div className="text-muted-foreground">Loading…</div></BrandedShell>;

  const rl = riskLevel(patient.risk_score);
  const style = RISK_STYLES[rl];

  const regenerate = async () => {
    await api.regeneratePlan(patient.id, patient.specialty, duration);
    toast.success("Plan regenerated", { description: `${duration}-day protocol drafted` });
    load();
  };

  const editTask = async (taskId, updates) => {
    // optimistic
    setPatient((prev) => ({
      ...prev,
      tasks: prev.tasks.map((t) => (t.id === taskId ? { ...t, ...updates } : t)),
    }));
    try { await api.updateTask(patient.id, taskId, updates); }
    catch { toast.error("Failed to save"); load(); }
  };

  const finalize = async () => {
    await api.finalizePlan(patient.id);
    toast.success("Protocol finalized");
    setShowFinalize(false);
    setConfirmedReview(false);
    load();
  };

  const unfinalize = async () => {
    await api.unfinalizePlan(patient.id);
    toast.success("Protocol unlocked for editing");
    load();
  };

  const patientUrl = `${window.location.origin}/${hospCode}/patient/${patient.id}`;
  const copyLink = async () => {
    await navigator.clipboard.writeText(patientUrl);
    toast.success("Patient link copied");
  };

  return (
    <BrandedShell>
      <HospitalHeader
        subtitle="Patient detail"
        testId="patient-detail-header"
        showBack
        backTo={`/${hospCode}/doctor`}
        backLabel="Portal"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 surface-card p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl border border-border bg-secondary text-foreground flex items-center justify-center text-sm font-semibold">
                {patient.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">{patient.name}</h2>
                <div className="text-sm text-muted-foreground">{patient.age} · {patient.gender} · {patient.surgery_type}</div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full border border-border bg-secondary">
                    <CalendarDays className="w-3 h-3 mr-1 inline" /> Day {patient.recovery_day}/{patient.care_plan_duration}
                  </span>
                  <span className={cn("text-[11px] font-semibold px-2 py-0.5 rounded-full border", style.badge)}>
                    {style.label} · {patient.risk_score}
                  </span>
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full border border-border bg-secondary">
                    {patient.specialty === "orthopedics" ? "Orthopedic AI" : "General Surgery AI"}
                  </span>
                  {patient.finalized && (
                    <span data-testid="finalized-badge" className="text-[11px] font-semibold px-2 py-0.5 rounded-full border brand-border brand-bg-soft brand-text inline-flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Finalized
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" className="rounded-full h-8 text-xs" onClick={copyLink} data-testid="copy-patient-link">
                <Copy className="w-3 h-3 mr-1.5" /> Copy patient link
              </Button>
              <Button asChild size="sm" className="rounded-full h-8 text-xs" data-testid="open-patient-portal">
                <a href={patientUrl} target="_blank" rel="noreferrer">
                  <ExternalLink className="w-3 h-3 mr-1.5" /> Open portal
                </a>
              </Button>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-0.5">Surgery date</div><div className="font-medium">{formatDate(patient.surgery_date)}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-0.5">Contact</div><div className="font-medium inline-flex items-center gap-1"><Phone className="w-3 h-3" />{patient.contact_phone || "—"}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-0.5">Last check-in</div><div className="font-medium">{timeAgo(patient.last_checkin)}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-0.5">Plan duration</div><div className="font-medium">{patient.care_plan_duration} days</div></div>
          </div>

          {patient.notes && (
            <div className="mt-5 border-t border-border pt-4 flex gap-3">
              <FileText className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
              <div className="text-sm text-muted-foreground whitespace-pre-line">{patient.notes}</div>
            </div>
          )}
        </div>

        {/* Plan generator */}
        <div className="surface-card p-6 shadow-sm flex flex-col gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Regenerate protocol</div>
            <div className="text-xs text-muted-foreground mt-1">Voice-drafted AI plan · surgeon-editable</div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium">Duration</label>
              <span className="text-xs font-semibold tabular-nums">{duration} days</span>
            </div>
            <Slider
              min={12} max={30} step={1}
              value={[duration]}
              onValueChange={([v]) => setDuration(v)}
              data-testid="detail-duration-slider"
              disabled={patient.finalized}
            />
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1 font-medium uppercase tracking-wider">
              <span>12</span><span>20</span><span>30</span>
            </div>
          </div>

          <div className="flex flex-col items-center pt-2 border-t border-border">
            <VoiceCaptureButton
              onComplete={regenerate}
              label="Voice regenerate"
              helper={patient.finalized ? "Unlock to regenerate" : "Tap to draft new plan"}
              size="sm"
              testId="regenerate-voice-btn"
            />
          </div>
        </div>
      </div>

      {/* Nurse assistance */}
      <div className="mb-8">
        <NurseCallScheduler patient={patient} onChange={load} />
      </div>

      {/* Plan header + finalize */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 brand-text" />
          <h3 className="text-lg font-semibold">Recovery protocol · {patient.care_plan_duration} days</h3>
          {patient.finalized && (
            <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border border-border bg-secondary text-muted-foreground gap-1">
              <Lock className="w-3 h-3" /> Read-only
            </span>
          )}
        </div>
        {patient.finalized ? (
          <Button variant="outline" size="sm" className="rounded-full text-xs h-8" onClick={unfinalize} data-testid="unfinalize-btn">
            <Unlock className="w-3.5 h-3.5 mr-1.5" /> Unlock protocol
          </Button>
        ) : (
          <Button size="sm" className="rounded-full text-xs h-8" onClick={() => setShowFinalize(true)} data-testid="finalize-btn">
            <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Finalize protocol
          </Button>
        )}
      </div>

      <div className="text-xs text-muted-foreground mb-4">
        {patient.finalized
          ? "Protocol is finalized. Hover any task's info icon for the clinical source."
          : "Hover a task to edit it, or click the pencil icon. Tap the info icon for the clinical source."}
      </div>

      <RecoveryCalendar
        tasks={patient.tasks || []}
        currentDay={patient.recovery_day}
        totalDays={patient.care_plan_duration || 20}
        onEdit={editTask}
        finalized={patient.finalized}
        nurseCallDays={patient.nurse_call_days || []}
      />

      <FooterDisclaimer />

      {/* Finalize dialog */}
      <Dialog open={showFinalize} onOpenChange={setShowFinalize}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Finalize protocol</DialogTitle>
            <DialogDescription>
              AI-Assisted Decision Support. Surgeon review required.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-muted-foreground space-y-3">
            <p>
              By finalizing, you confirm you have reviewed all {patient.tasks?.length || 0} items in this {patient.care_plan_duration}-day protocol for
              <span className="font-medium text-foreground"> {patient.name}</span> and take clinical responsibility for the plan.
            </p>
            <div className="rounded-lg border border-border bg-secondary/40 p-3 text-xs">
              After finalizing, line items become read-only. You can unlock the protocol at any time to make edits.
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                checked={confirmedReview}
                onCheckedChange={(v) => setConfirmedReview(Boolean(v))}
                data-testid="finalize-confirm-check"
              />
              <span className="text-sm text-foreground">
                I have reviewed all items and confirm this protocol is clinically appropriate.
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowFinalize(false)}>Cancel</Button>
            <Button disabled={!confirmedReview} onClick={finalize} data-testid="finalize-confirm-btn" className="rounded-full">
              Finalize
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </BrandedShell>
  );
}
